export type FacilityDetails = {
  name: string
  street: string
  city: string
  state: string
  zip: string
  fax: string
  phone: string
  // Carries the saved facility's Organization id once created/selected.
  organizationId?: string
}

// Composes the split facility address fields into a single display string.
export function formatFacilityAddress(facility: FacilityDetails): string {
  return [facility.street, facility.city, facility.state, facility.zip].filter(Boolean).join(', ')
}

// Loose name normalization for matching facilities against the patient's
// record locations ("been here" indicator): case/punctuation/whitespace
// insensitive, so "LabCorp - Daytona" matches "Labcorp Daytona".
export function normalizeFacilityName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

export type AuthorizationFormState = {
  facility: FacilityDetails
  recordTypes: string[]
  recordTypesOther: string // free text when 'Other' is among recordTypes
  dateRangeStart: string // MM/DD/YYYY
  dateRangeEnd: string
  purpose: string
  purposeOther: string // free text when purpose === 'Other'
  sensitiveTypes: string[] // subset of SENSITIVE_TYPE_OPTIONS; empty means none authorized
}

export function createInitialAuthorizationFormState(): AuthorizationFormState {
  return {
    facility: { name: '', street: '', city: '', state: '', zip: '', fax: '', phone: '' },
    recordTypes: [],
    recordTypesOther: '',
    dateRangeStart: '',
    dateRangeEnd: '',
    purpose: '',
    purposeOther: '',
    sensitiveTypes: [],
  }
}

// "Information Authorized for Release" — exact labels from the PD-920 authorization PDF.
export const RECORD_TYPE_OPTIONS = [
  'Complete Medical Record',
  'Progress Notes',
  'Laboratory Results',
  'Pathology Reports',
  'Imaging/Radiology Reports',
  'Medication History',
  'Problem List',
  'Consultation Reports',
  'Hospital Records',
  'Surgical Reports',
  'Billing Records',
  'Genetic Testing Results',
  'Other',
] as const

// "Sensitive Information" — exact labels from the PD-920 authorization PDF.
export const SENSITIVE_TYPE_OPTIONS = [
  'HIV/AIDS Information',
  'Mental/Behavioral Health Records',
  'Substance Use Disorder Records (where permitted by law)',
  'Genetic Testing Information',
  'Reproductive Health Information',
] as const

// "Purpose of Disclosure" — exact labels from the PD-920 authorization PDF.
export const PURPOSE_OPTIONS = [
  'Continuity of Care',
  'Treatment',
  'Cancer Screening and Preventive Care',
  'Care Coordination',
  'Insurance/Billing Operations',
  'Patient Request',
  'Other',
] as const

export const AUTHORIZATION_DOC_TYPE = {
  coding: [
    { system: 'http://loinc.org', code: '64292-6', display: 'Release of information consent' },
    {
      system: 'https://behuman.health/document-type',
      code: 'AuthorizationRelease',
      display: 'Authorization / Release',
    },
  ],
  text: 'Authorization / Release',
}

export const AUTHORIZATION_CONSENT_CATEGORY = {
  coding: [
    { system: 'http://loinc.org', code: '64292-6', display: 'Release of information consent' },
  ],
}
